import { FileText, X, Loader2 } from 'lucide-react';
import { Button, Badge } from '@powercore/ui';

// Exported type for generic API key connections
export interface IntegrationConfig {
  type: string;
  name: string;
  keyLabel: string;
  keyPlaceholder: string;
  helpText: string;
  secondKeyLabel?: string;
  secondKeyPlaceholder?: string;
}

// ============================================
// Brand SVG logos
// ============================================

export function GoogleDriveLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA"/>
      <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L3.45 44.6c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47"/>
      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l6.85 12.5 6.9 11.3z" fill="#EA4335"/>
      <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D"/>
      <path d="M59.8 49H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.5c1.6 0 3.15-.45 4.5-1.2L59.8 49z" fill="#2684FC"/>
      <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 24h27.5c0-1.55-.4-3.1-1.2-4.5l-12.7-18z" fill="#FFBA00"/>
    </svg>
  );
}

export function NotionLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-notion.png" alt="Notion" className={`${className} rounded-lg`} />;
}

function SlackLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 127 127" xmlns="http://www.w3.org/2000/svg">
      <path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2S.8 87.3.8 80s5.9-13.2 13.2-13.2h13.2V80zm6.6 0c0-7.3 5.9-13.2 13.2-13.2s13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2s-13.2-5.9-13.2-13.2V80z" fill="#E01E5A"/>
      <path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2S39.7.6 47 .6s13.2 5.9 13.2 13.2V27H47zm0 6.7c7.3 0 13.2 5.9 13.2 13.2s-5.9 13.2-13.2 13.2H14c-7.3 0-13.2-5.9-13.2-13.2S6.7 33.7 14 33.7h33z" fill="#36C5F0"/>
      <path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2s13.2 5.9 13.2 13.2-5.9 13.2-13.2 13.2H99.9V46.9zm-6.6 0c0 7.3-5.9 13.2-13.2 13.2s-13.2-5.9-13.2-13.2V14c0-7.3 5.9-13.2 13.2-13.2s13.2 5.9 13.2 13.2v32.9z" fill="#2EB67D"/>
      <path d="M80.1 99.8c7.3 0 13.2 5.9 13.2 13.2s-5.9 13.2-13.2 13.2-13.2-5.9-13.2-13.2V99.8h13.2zm0-6.6c-7.3 0-13.2-5.9-13.2-13.2s5.9-13.2 13.2-13.2h33c7.3 0 13.2 5.9 13.2 13.2s-5.9 13.2-13.2 13.2h-33z" fill="#ECB22E"/>
    </svg>
  );
}

export function TldvLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-tldv.png" alt="tl;dv" className={`${className} rounded-full`} />;
}

export function FathomLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-fathom.png" alt="Fathom" className={`${className} rounded-lg`} />;
}

export function GongLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-gong.png" alt="Gong" className={`${className} rounded-lg`} />;
}

export function FirefliesLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-fireflies.png" alt="Fireflies.ai" className={`${className} rounded-lg`} />;
}

export function OtterLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-otter.png" alt="Otter.ai" className={className} />;
}

function LindyLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="8" fill="#6366F1"/>
      <path d="M14 12h3v13h9v3H14V12z" fill="white"/>
    </svg>
  );
}

export function AvomaLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-avoma.png" alt="Avoma" className={`${className} rounded-lg`} />;
}

export function GranolaLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-granola.png" alt="Granola" className={`${className} rounded-lg`} />;
}

export function GoogleMeetLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 0C10.745 0 0 10.745 0 24s10.745 24 24 24 24-10.745 24-24S37.255 0 24 0z" fill="#00832D" fillOpacity="0"/>
      <path d="M12 16.8v14.4c0 1.32 1.08 2.4 2.4 2.4h14.4v-4.8H16.8V16.8H12z" fill="#00832D"/>
      <path d="M28.8 16.8V12H14.4c-1.32 0-2.4 1.08-2.4 2.4v14.4h4.8V16.8h12z" fill="#0066DA"/>
      <path d="M28.8 28.8h-12V33.6h14.4c1.32 0 2.4-1.08 2.4-2.4v-2.4l-4.8 3.2v-3.2z" fill="#2684FC"/>
      <path d="M33.6 14.4L28.8 17.6v12.8l4.8 3.2V14.4z" fill="#00AC47"/>
      <path d="M36 14.4h-2.4v19.2H36c1.32 0 2.4-1.08 2.4-2.4V16.8c0-1.32-1.08-2.4-2.4-2.4z" fill="#FFBA00"/>
    </svg>
  );
}

export function ZoomLogo({ className = 'w-6 h-6' }: { className?: string }) {
  return <img src="/logo-zoom.png" alt="Zoom" className={`${className} rounded-lg`} />;
}

// ============================================
// Component
// ============================================

interface ConnectorOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'available' | 'beta' | 'coming_soon' | 'new';
  onConnect?: () => void;
}

interface ConnectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnectGoogleDrive: () => void;
  onConnectGoogleMeet?: () => void;
  onAddManualNote: () => void;
  onConnectTldv?: () => void;
  onConnectIntegration: (config: IntegrationConfig) => void;
  onConnectGranolaOAuth?: () => void;
  onConnectOtterOAuth?: () => void;
  connectingGoogle?: boolean;
  connectingGoogleMeet?: boolean;
  connectingGranola?: boolean;
  connectingOtter?: boolean;
}

export function ConnectorModal({
  isOpen,
  onClose,
  onConnectGoogleDrive,
  onConnectGoogleMeet,
  onAddManualNote,
  onConnectTldv,
  onConnectIntegration,
  onConnectGranolaOAuth,
  onConnectOtterOAuth,
  connectingGoogle,
  connectingGoogleMeet,
  connectingGranola,
  connectingOtter,
}: ConnectorModalProps) {
  if (!isOpen) return null;

  const documents: ConnectorOption[] = [
    {
      id: 'google-drive',
      name: 'Google Drive',
      description: connectingGoogle ? 'Redirecting to Google...' : 'Sync documents and notes from Google Docs',
      icon: connectingGoogle ? <Loader2 className="w-6 h-6 animate-spin text-mazo-400" /> : <GoogleDriveLogo />,
      status: connectingGoogle ? 'coming_soon' : 'available',
      onConnect: onConnectGoogleDrive,
    },
    {
      id: 'notion',
      name: 'Notion',
      description: 'Import pages and notes from your workspace',
      icon: <NotionLogo />,
      status: 'beta',
      onConnect: () => onConnectIntegration({
        type: 'notion',
        name: 'Notion',
        keyLabel: 'Internal Connection Token',
        keyPlaceholder: 'ntn_...',
        helpText: 'Go to notion.so/my-integrations → create a new connection → copy the Internal Connection Secret. Then share the pages you want to import with that connection.',
      }),
    },
    {
      id: 'manual',
      name: 'Manual Entry',
      description: 'Add your notes and ideas directly',
      icon: <div className="w-6 h-6 flex items-center justify-center"><FileText className="w-6 h-6 text-gray-600" /></div>,
      status: 'available',
      onConnect: onAddManualNote,
    },
  ];

  const meetings: ConnectorOption[] = [
    {
      id: 'google-meet',
      name: 'Google Meet',
      description: connectingGoogleMeet ? 'Redirecting to Google...' : 'Import recordings from Google Meet',
      icon: connectingGoogleMeet ? <Loader2 className="w-6 h-6 animate-spin text-mazo-400" /> : <GoogleMeetLogo />,
      status: connectingGoogleMeet ? 'coming_soon' : 'new',
      onConnect: connectingGoogleMeet ? undefined : onConnectGoogleMeet,
    },
    {
      id: 'granola',
      name: 'Granola',
      description: connectingGranola ? 'Redirecting to Granola...' : 'AI meeting notes and transcripts',
      icon: connectingGranola ? <Loader2 className="w-6 h-6 animate-spin text-mazo-400" /> : <GranolaLogo />,
      status: connectingGranola ? 'coming_soon' : 'new',
      onConnect: connectingGranola ? undefined : onConnectGranolaOAuth,
    },
    {
      id: 'otter',
      name: 'Otter.ai',
      description: connectingOtter ? 'Redirecting to Otter.ai...' : 'Import meeting transcriptions and notes',
      icon: connectingOtter ? <Loader2 className="w-6 h-6 animate-spin text-mazo-400" /> : <OtterLogo />,
      status: connectingOtter ? 'coming_soon' : 'new',
      onConnect: connectingOtter ? undefined : onConnectOtterOAuth,
    },
    {
      id: 'tldv',
      name: 'tl;dv',
      description: 'Sync meeting recordings and transcripts',
      icon: <TldvLogo />,
      status: 'available',
      onConnect: onConnectTldv,
    },
    {
      id: 'fathom',
      name: 'Fathom',
      description: 'Import AI-generated meeting notes and highlights',
      icon: <FathomLogo />,
      status: 'beta',
      onConnect: () => onConnectIntegration({
        type: 'fathom',
        name: 'Fathom',
        keyLabel: 'API Key',
        keyPlaceholder: 'Enter your Fathom API key',
        helpText: 'In Fathom, go to Settings → My Settings → API Access → click "Add" → Generate API Key. Copy the key and paste it here.',
      }),
    },
    {
      id: 'gong',
      name: 'Gong',
      description: 'Import call recordings and conversation intelligence',
      icon: <GongLogo />,
      status: 'beta',
      onConnect: () => onConnectIntegration({
        type: 'gong',
        name: 'Gong',
        keyLabel: 'Access Key',
        keyPlaceholder: 'Enter your Gong access key',
        helpText: 'In Gong, go to Settings → API (admin required) → click "Get API Key". You\'ll receive an Access Key and an Access Key Secret. Enter both below.',
        secondKeyLabel: 'Access Key Secret',
        secondKeyPlaceholder: 'Enter your Gong access key secret',
      }),
    },
    {
      id: 'fireflies',
      name: 'Fireflies.ai',
      description: 'Import AI meeting transcripts and summaries',
      icon: <FirefliesLogo />,
      status: 'beta',
      onConnect: () => onConnectIntegration({
        type: 'fireflies',
        name: 'Fireflies.ai',
        keyLabel: 'API Key',
        keyPlaceholder: 'Enter your Fireflies API key',
        helpText: 'In Fireflies, go to Settings → API → find "Fireflies API" → copy your API Key. Requires a Business plan or above.',
      }),
    },
    {
      id: 'avoma',
      name: 'Avoma',
      description: 'Import AI meeting notes and conversation insights',
      icon: <AvomaLogo />,
      status: 'beta',
      onConnect: () => onConnectIntegration({
        type: 'avoma',
        name: 'Avoma',
        keyLabel: 'API Key',
        keyPlaceholder: 'Enter your Avoma API key',
        helpText: 'In Avoma, go to Settings → Organization → Developer (admin required) → click "Create API Key". Copy and paste it here.',
      }),
    },
    {
      id: 'zoom',
      name: 'Zoom',
      description: 'Import cloud recording transcripts from Zoom meetings',
      icon: <ZoomLogo />,
      status: 'beta',
      onConnect: () => onConnectIntegration({
        type: 'zoom',
        name: 'Zoom',
        keyLabel: 'Access Token',
        keyPlaceholder: 'Enter your Zoom access token',
        helpText: 'In the Zoom App Marketplace, create a Server-to-Server OAuth app or use a personal access token. Grant the "cloud_recording:read:list_user_recordings" and "cloud_recording:read:list_recording_files" scopes.',
      }),
    },
  ];

  const other: ConnectorOption[] = [
    {
      id: 'lindy',
      name: 'Lindy.ai',
      description: 'Import content from your AI assistant workflows',
      icon: <LindyLogo />,
      status: 'coming_soon',
    },
    {
      id: 'slack',
      name: 'Slack',
      description: 'Import insights from your conversations',
      icon: <SlackLogo />,
      status: 'coming_soon',
    },
  ];

  const renderConnector = (connector: ConnectorOption) => {
    const isDisabled = connector.status === 'coming_soon';
    return (
      <button
        key={connector.id}
        onClick={!isDisabled ? connector.onConnect : undefined}
        disabled={isDisabled}
        className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 transition-all ${
          isDisabled
            ? 'opacity-50 cursor-not-allowed bg-gray-50'
            : 'hover:border-mazo-400 hover:bg-mazo-50 hover:shadow-sm cursor-pointer active:scale-[0.99]'
        }`}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center">
          {connector.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 text-sm">{connector.name}</span>
            {connector.status === 'new' && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-mazo-100 text-mazo-700 rounded">New</span>
            )}
            {connector.status === 'beta' && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-mazo-100 text-mazo-700 rounded">Beta</span>
            )}
            {connector.status === 'coming_soon' && (
              <Badge variant="secondary">Coming Soon</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{connector.description}</p>
        </div>
      </button>
    );
  };

  const renderSection = (title: string, connectors: ConnectorOption[]) => (
    <div>
      <h3 className="text-[11px] font-medium text-gray-400 uppercase tracking-wider px-1 mb-1.5">{title}</h3>
      <div className="space-y-1">
        {connectors.map(renderConnector)}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Connect a Source</h2>
            <p className="text-sm text-gray-500 mt-1">Choose where to import your content from</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-4">
          {renderSection('Documents & Content', documents)}
          {renderSection('Meetings & Calls', meetings)}
          {renderSection('Other', other)}
        </div>

        <div className="p-4 border-t bg-gray-50 rounded-b-xl">
          <Button variant="ghost" onClick={onClose} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConnectorModal;
